import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ClipboardList,
  Eye,
  Loader2,
  MapPin,
  PackageOpen,
  Plus,
  Printer,
  RefreshCw,
  Search,
  ShoppingBag,
  TrendingUp,
  Truck,
  Wallet,
} from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { useAdmin } from "../context/AdminContext";
import { formatDate, formatRupiah } from "../lib/format";
import { printShippingLabel } from "../lib/printLabel";
import { salesApi, shippingApi } from "../lib/api";

const emptyCustomer = {
  full_name: "",
  phone: "",
  address: "",
  province_id: "",
  province: "",
  city_id: "",
  district_id: "",
  subdistrict_id: "",
  kelurahan: "",
  kecamatan: "",
  kota: "",
  zip_code: "",
  notes: "",
};

const emptyForm = {
  customer: emptyCustomer,
  items: [{ product_id: "", quantity: 1, unit_price: "" }],
  discount_code: "none",
  source_channel: "manual",
  shipping_cost: "",
  payment_method: "cash",
  payment_status: "dibayar",
  order_status: "selesai",
  courier_name: "",
  courier_service: "",
  tracking_number: "",
  notes: "",
};

const toneClass = {
  success: "bg-emerald-100 text-emerald-700 border-emerald-200",
  warning: "bg-amber-100 text-amber-700 border-amber-200",
  danger: "bg-rose-100 text-rose-700 border-rose-200",
  info: "bg-sky-100 text-sky-700 border-sky-200",
};

const statusLabel = {
  menunggu: { label: "Menunggu", tone: "warning" },
  dibayar: { label: "Dibayar", tone: "success" },
  diproses: { label: "Diproses", tone: "info" },
  dikirim: { label: "Dikirim", tone: "info" },
  selesai: { label: "Selesai", tone: "success" },
  batal: { label: "Batal", tone: "danger" },
};

const orderStatusKeys = ["menunggu", "diproses", "dikirim", "selesai", "batal"];
const PAGE_SIZE = 10;

const discountTargetLabel = {
  product: "Produk",
  shipping: "Ongkir",
};

const calculateDiscountBreakdown = (discount, itemsSubtotal, shippingCost) => {
  if (!discount || !discount.is_active || itemsSubtotal < Number(discount.min_order_amount || 0)) {
    return { total: 0, product: 0, shipping: 0, target: discount?.discount_target || "product" };
  }

  const target = discount.discount_target === "shipping" ? "shipping" : "product";
  const baseAmount = target === "shipping" ? Number(shippingCost || 0) : Number(itemsSubtotal || 0);
  const rawAmount = discount.discount_type === "percentage"
    ? baseAmount * (Number(discount.value || 0) / 100)
    : Number(discount.value || 0);
  const amount = Math.max(0, Math.min(rawAmount, discount.max_discount ?? baseAmount, baseAmount));

  return {
    total: amount,
    product: target === "product" ? amount : 0,
    shipping: target === "shipping" ? amount : 0,
    target,
  };
};

const isPaymentCompleted = (sale) => Boolean(sale?.payment_completed_at);

const StatusBadge = ({ status }) => {
  const meta = statusLabel[status] || { label: status || "-", tone: "info" };
  return (
    <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium ${toneClass[meta.tone]}`}>
      {meta.label}
    </span>
  );
};

const AdminSalesPage = () => {
  const { products, refreshData } = useAdmin();
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState({
    total_transactions: 0,
    paid_transactions: 0,
    net_revenue: 0,
    total_discount: 0,
  });
  const [discounts, setDiscounts] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [shipment, setShipment] = useState(null);
  const [shipmentForm, setShipmentForm] = useState({
    courier_code: "",
    courier_name: "",
    courier_service: "",
    courier_etd: "",
    tracking_number: "",
  });
  const [courierOptions, setCourierOptions] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [locations, setLocations] = useState({
    provinces: [],
    cities: [],
    districts: [],
    subdistricts: [],
  });
  const [locationLoading, setLocationLoading] = useState(false);

  const activeProducts = useMemo(
    () => products.filter((product) => product.is_active !== false && product.stock > 0),
    [products],
  );

  const loadSales = async () => {
    try {
      setLoading(true);
      const [salesData, summaryData, discountsData] = await Promise.all([
        salesApi.getAll({
          search,
          status_filter: status,
          skip: (page - 1) * PAGE_SIZE,
          limit: PAGE_SIZE + 1,
        }),
        salesApi.getSummary(),
        salesApi.getDiscounts(),
      ]);
      const rows = salesData || [];
      setSales(rows.slice(0, PAGE_SIZE));
      setHasNextPage(rows.length > PAGE_SIZE);
      setSummary(summaryData || {});
      setDiscounts(discountsData || []);
    } catch (error) {
      toast.error(error.message || "Gagal memuat data penjualan");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(loadSales, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, page]);

  useEffect(() => {
    if (!shipment) return;
    setShipmentForm({
      courier_code: shipment.courier_code || "",
      courier_name: shipment.courier_name || "",
      courier_service: shipment.courier_service || "",
      courier_etd: shipment.courier_etd || "",
      tracking_number: shipment.tracking_number || "",
    });
  }, [shipment]);

  useEffect(() => {
    shippingApi.getCouriers().then((data) => setCourierOptions(data || [])).catch(() => {});
  }, []);

  const selectedItems = form.items.map((item) => {
    const product = products.find((p) => String(p.id) === String(item.product_id));
    const price = item.unit_price === "" ? Number(product?.price || 0) : Number(item.unit_price);
    const quantity = Number(item.quantity || 0);
    return { ...item, product, price, subtotal: price * quantity };
  });

  const formSubtotal = selectedItems.reduce((sum, item) => sum + item.subtotal, 0);
  const shippingCost = Number(form.shipping_cost || 0);
  const selectedDiscount = discounts.find((d) => d.code === form.discount_code);
  const estimatedDiscount = calculateDiscountBreakdown(selectedDiscount, formSubtotal, shippingCost);
  const formTotal = Math.max(formSubtotal + shippingCost - estimatedDiscount.total, 0);

  const setCustomerField = (key, value) => {
    setForm((current) => ({
      ...current,
      customer: { ...current.customer, [key]: value },
    }));
  };

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const loadProvinces = async () => {
    try {
      setLocationLoading(true);
      const provinces = await shippingApi.getProvinces();
      setLocations((current) => ({ ...current, provinces: provinces || [] }));
    } catch (error) {
      toast.error(error.message || "Gagal memuat provinsi RajaOngkir");
    } finally {
      setLocationLoading(false);
    }
  };

  const loadCities = async (provinceId) => {
    if (!provinceId) return;
    try {
      setLocationLoading(true);
      const cities = await shippingApi.getCities({ province_id: provinceId });
      setLocations((current) => ({ ...current, cities: cities || [], districts: [], subdistricts: [] }));
    } catch (error) {
      toast.error(error.message || "Gagal memuat kota RajaOngkir");
    } finally {
      setLocationLoading(false);
    }
  };

  const loadDistricts = async (cityId) => {
    if (!cityId) return;
    try {
      setLocationLoading(true);
      const districts = await shippingApi.getDistricts({ city_id: cityId });
      setLocations((current) => ({ ...current, districts: districts || [], subdistricts: [] }));
    } catch (error) {
      toast.error(error.message || "Gagal memuat kecamatan RajaOngkir");
    } finally {
      setLocationLoading(false);
    }
  };

  const loadSubdistricts = async (districtId) => {
    if (!districtId) return;
    try {
      setLocationLoading(true);
      const subdistricts = await shippingApi.getSubdistricts({ district_id: districtId });
      setLocations((current) => ({ ...current, subdistricts: subdistricts || [] }));
    } catch (error) {
      toast.error(error.message || "Gagal memuat kelurahan RajaOngkir");
    } finally {
      setLocationLoading(false);
    }
  };

  const selectProvince = (value) => {
    const item = locations.provinces.find((row) => String(row.external_id) === String(value));
    setForm((current) => ({
      ...current,
      customer: {
        ...current.customer,
        province_id: value,
        province: item?.name || "",
        city_id: "",
        district_id: "",
        subdistrict_id: "",
        kota: "",
        kecamatan: "",
        kelurahan: "",
        zip_code: "",
      },
    }));
    loadCities(value);
  };

  const selectCity = (value) => {
    const item = locations.cities.find((row) => String(row.external_id) === String(value));
    setForm((current) => ({
      ...current,
      customer: {
        ...current.customer,
        city_id: value,
        district_id: "",
        subdistrict_id: "",
        kota: item?.name || "",
        kecamatan: "",
        kelurahan: "",
        zip_code: "",
      },
    }));
    loadDistricts(value);
  };

  const selectDistrict = (value) => {
    const item = locations.districts.find((row) => String(row.external_id) === String(value));
    setForm((current) => ({
      ...current,
      customer: {
        ...current.customer,
        district_id: value,
        subdistrict_id: "",
        kecamatan: item?.name || "",
        kelurahan: "",
        zip_code: "",
      },
    }));
    loadSubdistricts(value);
  };

  const selectSubdistrict = (value) => {
    const item = locations.subdistricts.find((row) => String(row.external_id) === String(value));
    setForm((current) => ({
      ...current,
      customer: {
        ...current.customer,
        subdistrict_id: value,
        kelurahan: item?.name || "",
        zip_code: item?.zip_code || "",
      },
    }));
  };

  const setItemField = (index, key, value) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item,
      ),
    }));
  };

  const addItem = () => {
    setForm((current) => ({
      ...current,
      items: [...current.items, { product_id: "", quantity: 1, unit_price: "" }],
    }));
  };

  const removeItem = (index) => {
    setForm((current) => ({
      ...current,
      items: current.items.length === 1 ? current.items : current.items.filter((_, i) => i !== index),
    }));
  };

  const openNew = () => {
    setForm({
      ...emptyForm,
      customer: { ...emptyCustomer },
      items: [{ product_id: activeProducts[0]?.id ? String(activeProducts[0].id) : "", quantity: 1, unit_price: "" }],
    });
    if (locations.provinces.length === 0) {
      loadProvinces();
    }
    setOpen(true);
  };

  const onSave = async (event) => {
    event.preventDefault();
    if (!form.customer.full_name.trim()) return toast.error("Nama customer wajib diisi");
    if (!form.customer.phone.trim()) return toast.error("Nomor HP / WhatsApp wajib diisi");
    if (!form.customer.address.trim()) return toast.error("Alamat lengkap wajib diisi");
    if (!form.customer.city_id || !form.customer.district_id || !form.customer.subdistrict_id) {
      return toast.error("Kota, kecamatan, dan kelurahan wajib dipilih dari RajaOngkir");
    }
    if (form.items.some((item) => !item.product_id || Number(item.quantity) < 1)) {
      return toast.error("Produk dan qty wajib valid");
    }

    const payload = {
      ...form,
      discount_code: form.discount_code === "none" ? null : form.discount_code,
      shipping_cost: Number(form.shipping_cost || 0),
      customer: Object.fromEntries(
        Object.entries(form.customer).map(([key, value]) => [key, typeof value === "string" ? value.trim() : value]),
      ),
      items: form.items.map((item) => ({
        product_id: Number(item.product_id),
        quantity: Number(item.quantity),
        unit_price: item.unit_price === "" ? null : Number(item.unit_price),
      })),
    };

    try {
      setSaving(true);
      await salesApi.create(payload);
      toast.success("Penjualan berhasil dicatat");
      setOpen(false);
      await Promise.all([loadSales(), refreshData()]);
    } catch (error) {
      toast.error(error.message || "Gagal menyimpan penjualan");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (sale, orderStatus, extra = {}) => {
    if (orderStatus === "dikirim" && !(extra.tracking_number || sale.tracking_number || "").trim()) {
      toast.error("Nomor resi wajib diisi sebelum status dikirim");
      return;
    }

    try {
      const paymentStatus = orderStatus === "menunggu" ? "menunggu" : sale.payment_status === "menunggu" ? "dibayar" : sale.payment_status;
      const updated = await salesApi.updateStatus(sale.id, {
        ...extra,
        order_status: orderStatus,
        payment_status: paymentStatus,
      });
      if (detail?.id === updated.id) setDetail(updated);
      if (shipment?.id === updated.id) setShipment(updated);
      await loadSales();
      toast.success("Status penjualan diperbarui");
    } catch (error) {
      toast.error(error.message || "Gagal update status");
    }
  };

  const updateShipment = async () => {
    if (!shipment) return;
    if (!shipmentForm.tracking_number.trim()) {
      toast.error("Nomor resi wajib diisi");
      return;
    }

    const courierMatch = courierOptions.find((c) => c.code === shipmentForm.courier_code);

    try {
      const updated = await salesApi.updateStatus(shipment.id, {
        courier_code: shipmentForm.courier_code.trim(),
        courier_name: courierMatch ? courierMatch.name : shipmentForm.courier_name.trim(),
        courier_service: shipmentForm.courier_service.trim(),
        courier_etd: shipmentForm.courier_etd.trim(),
        tracking_number: shipmentForm.tracking_number.trim(),
        order_status: "dikirim",
        payment_status: shipment.payment_status === "menunggu" ? "dibayar" : shipment.payment_status,
      });
      setShipment(updated);
      if (detail?.id === updated.id) setDetail(updated);
      await loadSales();
      toast.success("Data pengiriman disimpan");
    } catch (error) {
      toast.error(error.message || "Gagal menyimpan data pengiriman");
    }
  };

  return (
    <AdminLayout>
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8" data-testid="admin-sales-page">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-rose-50 text-rose-700">
              <ClipboardList className="h-6 w-6" />
            </span>
            <div>
              <h1 className="font-display text-3xl font-semibold text-stone-900 sm:text-4xl">Penjualan</h1>
              <p className="mt-1 text-base text-stone-600">
                Menampilkan transaksi yang pembayarannya sudah selesai berdasarkan `payment_completed_at`.
              </p>
            </div>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="h-12 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700">
                <Plus className="mr-2 h-5 w-5" /> Tambah Penjualan
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Tambah Penjualan</DialogTitle>
              </DialogHeader>
              <form onSubmit={onSave} className="space-y-6 pt-2">
                <section className="rounded-2xl border border-stone-200 bg-white p-5">
                  <h3 className="text-lg font-semibold text-stone-900">Data Customer</h3>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Label>Nama Lengkap</Label>
                      <Input value={form.customer.full_name} onChange={(e) => setCustomerField("full_name", e.target.value)} placeholder="Contoh: Ibu Siti Aminah" className="h-12 rounded-xl" />
                    </div>
                    <div>
                      <Label>Nomor HP / WhatsApp</Label>
                      <Input value={form.customer.phone} onChange={(e) => setCustomerField("phone", e.target.value)} placeholder="08xxxxxxxxxx" className="h-12 rounded-xl" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label>Alamat Lengkap</Label>
                      <Textarea value={form.customer.address} onChange={(e) => setCustomerField("address", e.target.value)} placeholder="Jalan, nomor rumah, RT/RW, patokan" className="min-h-24 rounded-xl" />
                    </div>
                    <div>
                      <Label>Provinsi</Label>
                      <Select value={String(form.customer.province_id || "")} onValueChange={selectProvince}>
                        <SelectTrigger className="h-12 rounded-xl">
                          <SelectValue placeholder={locationLoading ? "Memuat..." : "Pilih provinsi"} />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.provinces.map((item) => (
                            <SelectItem key={item.external_id} value={String(item.external_id)}>
                              {item.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Kota / Kabupaten</Label>
                      <Select value={String(form.customer.city_id || "")} onValueChange={selectCity} disabled={!form.customer.province_id}>
                        <SelectTrigger className="h-12 rounded-xl">
                          <SelectValue placeholder="Pilih kota" />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.cities.map((item) => (
                            <SelectItem key={item.external_id} value={String(item.external_id)}>
                              {item.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Kecamatan</Label>
                      <Select value={String(form.customer.district_id || "")} onValueChange={selectDistrict} disabled={!form.customer.city_id}>
                        <SelectTrigger className="h-12 rounded-xl">
                          <SelectValue placeholder="Pilih kecamatan" />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.districts.map((item) => (
                            <SelectItem key={item.external_id} value={String(item.external_id)}>
                              {item.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Kelurahan</Label>
                      <Select value={String(form.customer.subdistrict_id || "")} onValueChange={selectSubdistrict} disabled={!form.customer.district_id}>
                        <SelectTrigger className="h-12 rounded-xl">
                          <SelectValue placeholder="Pilih kelurahan" />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.subdistricts.map((item) => (
                            <SelectItem key={item.external_id} value={String(item.external_id)}>
                              {item.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2 rounded-xl border border-emerald-100 bg-emerald-50 p-4">
                      <div className="flex items-start gap-3">
                        <MapPin className="mt-0.5 h-5 w-5 text-emerald-700" />
                        <div className="grid flex-1 gap-3 sm:grid-cols-2">
                          <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Kodepos</p>
                            <p className="mt-1 text-base font-semibold text-stone-900">{form.customer.zip_code || "-"}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Lokasi Terpilih</p>
                            <p className="mt-1 text-sm font-medium text-stone-900">
                              {[form.customer.kelurahan, form.customer.kecamatan, form.customer.kota].filter(Boolean).join(", ") || "-"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <Label>Catatan untuk penjual / kurir</Label>
                      <Textarea value={form.customer.notes} onChange={(e) => setCustomerField("notes", e.target.value)} placeholder="Contoh: Taruh di depan pagar, hubungi sebelum datang" className="min-h-20 rounded-xl" />
                    </div>
                  </div>
                </section>

                <section className="rounded-2xl border border-stone-200 bg-white p-5">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-stone-900">Produk</h3>
                    <Button type="button" variant="outline" onClick={addItem} className="h-10 rounded-xl">
                      <Plus className="mr-2 h-4 w-4" /> Tambah Item
                    </Button>
                  </div>
                  <div className="mt-4 space-y-3">
                    {form.items.map((item, index) => {
                      const product = products.find((p) => String(p.id) === String(item.product_id));
                      return (
                        <div key={index} className="grid gap-3 rounded-xl border border-stone-200 bg-stone-50 p-3 md:grid-cols-[1fr_120px_150px_90px]">
                          <Select value={String(item.product_id)} onValueChange={(value) => setItemField(index, "product_id", value)}>
                            <SelectTrigger className="h-12 rounded-xl bg-white">
                              <SelectValue placeholder="Pilih produk" />
                            </SelectTrigger>
                            <SelectContent>
                              {activeProducts.map((productItem) => (
                                <SelectItem key={productItem.id} value={String(productItem.id)}>
                                  {productItem.name} - stok {productItem.stock}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input type="number" min="1" max={product?.stock || undefined} value={item.quantity} onChange={(e) => setItemField(index, "quantity", e.target.value)} className="h-12 rounded-xl bg-white" />
                          <Input type="number" min="0" value={item.unit_price} onChange={(e) => setItemField(index, "unit_price", e.target.value)} placeholder={product ? String(product.price) : "Harga"} className="h-12 rounded-xl bg-white" />
                          <Button type="button" variant="outline" onClick={() => removeItem(index)} className="h-12 rounded-xl border-rose-200 text-rose-700">
                            Hapus
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </section>

                <section className="rounded-2xl border border-stone-200 bg-white p-5">
                  <h3 className="text-lg font-semibold text-stone-900">Pembayaran & Kanal</h3>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <Label>Kanal</Label>
                      <Select value={form.source_channel} onValueChange={(value) => setField("source_channel", value)}>
                        <SelectTrigger className="h-12 rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">Manual</SelectItem>
                          <SelectItem value="web">Web</SelectItem>
                          <SelectItem value="whatsapp">WhatsApp</SelectItem>
                          <SelectItem value="marketplace">Marketplace</SelectItem>
                          <SelectItem value="reseller">Reseller</SelectItem>
                          <SelectItem value="offline">Offline</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Metode Bayar</Label>
                      <Input value={form.payment_method} onChange={(e) => setField("payment_method", e.target.value)} className="h-12 rounded-xl" />
                    </div>
                    <div>
                      <Label>Diskon / Kupon</Label>
                      <Select value={form.discount_code} onValueChange={(value) => setField("discount_code", value)}>
                        <SelectTrigger className="h-12 rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Tanpa Diskon</SelectItem>
                          {discounts.filter((discount) => discount.is_active).map((discount) => (
                            <SelectItem key={discount.id} value={discount.code}>
                              {discount.code} - {discountTargetLabel[discount.discount_target] || "Produk"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Ongkir</Label>
                      <Input type="number" min="0" value={form.shipping_cost} onChange={(e) => setField("shipping_cost", e.target.value)} className="h-12 rounded-xl" />
                    </div>
                    <div>
                      <Label>Kurir</Label>
                      <Input value={form.courier_name} onChange={(e) => setField("courier_name", e.target.value)} placeholder="JNE, J&T, ambil sendiri" className="h-12 rounded-xl" />
                    </div>
                    <div>
                      <Label>Status Pesanan</Label>
                      <Select value={form.order_status} onValueChange={(value) => setField("order_status", value)}>
                        <SelectTrigger className="h-12 rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {orderStatusKeys.map((key) => (
                            <SelectItem key={key} value={key}>{statusLabel[key].label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-5 rounded-xl bg-emerald-50 p-4 text-sm text-stone-700">
                    Subtotal {formatRupiah(formSubtotal)} - Diskon Produk {formatRupiah(estimatedDiscount.product)} + Ongkir {formatRupiah(shippingCost)} - Diskon Ongkir {formatRupiah(estimatedDiscount.shipping)} ={" "}
                    <span className="font-semibold text-emerald-700">{formatRupiah(formTotal)}</span>
                  </div>
                </section>

                <DialogFooter className="sticky bottom-0 rounded-2xl border border-stone-200 bg-white/95 p-4 backdrop-blur">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="h-12 rounded-xl">Batal</Button>
                  <Button type="submit" disabled={saving} className="h-12 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700">
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan Penjualan
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-4">
          <StatCard icon={ShoppingBag} label="Total Transaksi Paid" value={summary.total_transactions || 0} tone="bg-emerald-50 text-emerald-700" />
          <StatCard icon={TrendingUp} label="Pembayaran Selesai" value={summary.paid_transactions || 0} tone="bg-sky-50 text-sky-700" />
          <StatCard icon={Wallet} label="Pendapatan Bersih" value={formatRupiah(summary.net_revenue)} tone="bg-rose-50 text-rose-700" />
          <StatCard icon={ClipboardList} label="Total Diskon" value={formatRupiah(summary.total_discount)} tone="bg-amber-50 text-amber-700" />
        </div>

        <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-400" />
            <Input
              className="h-12 rounded-xl pl-11"
              placeholder="Cari invoice, nama customer, atau nomor HP..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <Select
            value={status}
            onValueChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
          >
            <SelectTrigger className="h-12 rounded-xl sm:w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              {orderStatusKeys.map((key) => (
                <SelectItem key={key} value={key}>{statusLabel[key].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadSales} className="h-12 rounded-xl">
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-stone-200 bg-white p-12 text-center text-stone-500">
            <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin" /> Memuat penjualan...
          </div>
        ) : sales.length === 0 ? (
          <div className="rounded-2xl border border-stone-200 bg-white p-14 text-center">
            <PackageOpen className="mx-auto mb-3 h-16 w-16 text-stone-300" />
            <p className="font-display text-xl font-semibold text-stone-900">Belum ada penjualan</p>
            <p className="mt-1 text-stone-500">Klik Tambah Penjualan untuk mencatat transaksi dari kanal lain.</p>
          </div>
        ) : (
          <>
            <SalesTable sales={sales} products={products} onDetail={setDetail} onShipment={setShipment} onPrint={printShippingLabel} />
            <div className="mt-5 flex items-center justify-between rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
              <p className="text-sm text-stone-500">Halaman {page}</p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="h-10 rounded-xl"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  Sebelumnya
                </Button>
                <Button
                  variant="outline"
                  className="h-10 rounded-xl"
                  disabled={!hasNextPage || loading}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Berikutnya
                </Button>
              </div>
            </div>
          </>
        )}

        <Dialog open={!!detail} onOpenChange={(value) => !value && setDetail(null)}>
          <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Detail Penjualan</DialogTitle>
            </DialogHeader>
            {detail && (
              <div className="space-y-5 pt-2">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={detail.payment_status} />
                  <StatusBadge status={detail.order_status} />
                  <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-600">{detail.source_channel}</span>
                  {isPaymentCompleted(detail) && (
                    <Button
                      onClick={() => printShippingLabel(detail, products)}
                      className="ml-auto h-10 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
                    >
                      <Printer className="mr-2 h-4 w-4" /> Cetak Pesanan
                    </Button>
                  )}
                </div>
                <dl className="grid gap-4 text-base sm:grid-cols-2">
                  <Info label="Invoice" value={detail.invoice} />
                  <Info label="Tanggal Bayar" value={formatDate(detail.payment_completed_at || detail.sale_date)} />
                  <Info label="Customer" value={`${detail.customer.full_name} - ${detail.customer.phone}`} wide />
                  <Info
                    label="Kurir"
                    value={[detail.courier_name, detail.courier_service, detail.courier_etd].filter(Boolean).join(" - ")}
                  />
                  <Info label="Nomor Resi" value={detail.tracking_number || "Belum diisi"} />
                  <Info
                    label="Alamat"
                    value={[
                      detail.customer.address,
                      detail.customer.kelurahan,
                      detail.customer.kecamatan,
                      detail.customer.kota,
                      detail.customer.province,
                      detail.customer.zip_code,
                    ].filter(Boolean).join(", ")}
                    wide
                  />
                </dl>
                <div className="rounded-2xl border border-stone-200">
                  <div className="border-b border-stone-200 px-4 py-3 font-medium text-stone-900">Item Produk</div>
                  <div className="divide-y divide-stone-100">
                    {detail.items.map((item) => (
                      <div key={item.id} className="flex justify-between gap-4 px-4 py-3 text-sm">
                        <span className="font-medium text-stone-900">{item.product_name_snapshot} x {item.quantity}</span>
                        <span className="text-stone-600">{formatRupiah(item.subtotal)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-2xl bg-stone-50 p-4 text-sm">
                  <div className="flex justify-between"><span>Subtotal</span><span>{formatRupiah(detail.items_subtotal)}</span></div>
                  <div className="mt-2 flex justify-between"><span>Diskon Produk {detail.discount?.code && (detail.discount_target_snapshot || detail.discount?.discount_target) === "product" ? `(${detail.discount.code})` : ""}</span><span>-{formatRupiah(detail.product_discount_amount || 0)}</span></div>
                  <div className="mt-2 flex justify-between"><span>Ongkir</span><span>{formatRupiah(detail.shipping_cost)}</span></div>
                  <div className="mt-2 flex justify-between"><span>Diskon Ongkir {detail.discount?.code && (detail.discount_target_snapshot || detail.discount?.discount_target) === "shipping" ? `(${detail.discount.code})` : ""}</span><span>-{formatRupiah(detail.shipping_discount_amount || 0)}</span></div>
                  <div className="mt-3 flex justify-between border-t border-stone-200 pt-3 text-base font-semibold text-emerald-700"><span>Total</span><span>{formatRupiah(detail.total)}</span></div>
                </div>
                <div>
                  <p className="mb-2 text-sm text-stone-500">Ubah Status Pesanan</p>
                  <div className="flex flex-wrap gap-2">
                    {orderStatusKeys.map((key) => (
                      <Button key={key} variant={detail.order_status === key ? "default" : "outline"} onClick={() => updateStatus(detail, key)} className={`h-10 rounded-xl ${detail.order_status === key ? "bg-emerald-600 text-white hover:bg-emerald-700" : ""}`}>
                        {statusLabel[key].label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={!!shipment} onOpenChange={(value) => !value && setShipment(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Data Pengiriman</DialogTitle>
            </DialogHeader>
            {shipment && (
              <div className="space-y-5 pt-2">
                <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
                  <p className="font-semibold text-stone-900">{shipment.invoice}</p>
                  <p className="mt-1 text-sm text-stone-600">
                    {shipment.customer.full_name} - {shipment.customer.phone}
                  </p>
                  <p className="mt-2 text-sm text-stone-500">
                    {[shipment.customer.address, shipment.customer.kelurahan, shipment.customer.kecamatan, shipment.customer.kota, shipment.customer.province, shipment.customer.zip_code].filter(Boolean).join(", ")}
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label>Kurir</Label>
                    <Select
                      value={shipmentForm.courier_code}
                      onValueChange={(value) => {
                        const match = courierOptions.find((c) => c.code === value);
                        setShipmentForm((current) => ({
                          ...current,
                          courier_code: value,
                          courier_name: match ? match.name : value,
                        }));
                      }}
                    >
                      <SelectTrigger className="h-11 rounded-xl">
                        <SelectValue placeholder="Pilih kurir" />
                      </SelectTrigger>
                      <SelectContent>
                        {courierOptions.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Layanan</Label>
                    <Input
                      value={shipmentForm.courier_service}
                      onChange={(e) => setShipmentForm((current) => ({ ...current, courier_service: e.target.value }))}
                      placeholder="Contoh: REG, ECO, YES"
                      className="h-11 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label>Estimasi</Label>
                    <Select
                      value={shipmentForm.courier_etd}
                      onValueChange={(value) => setShipmentForm((current) => ({ ...current, courier_etd: value }))}
                    >
                      <SelectTrigger className="h-11 rounded-xl">
                        <SelectValue placeholder="Pilih estimasi" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-2 Hari">1-2 Hari</SelectItem>
                        <SelectItem value="2-4 Hari">2-4 Hari</SelectItem>
                        <SelectItem value="4-6 Hari">4-6 Hari</SelectItem>
                        <SelectItem value="6-10 Hari">6-10 Hari</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Nomor Resi</Label>
                    <Input
                      value={shipmentForm.tracking_number}
                      onChange={(e) => setShipmentForm((current) => ({ ...current, tracking_number: e.target.value }))}
                      placeholder="Masukkan nomor resi"
                      className="h-11 rounded-xl"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShipment(null)} className="h-11 rounded-xl">
                    Batal
                  </Button>
                  <Button
                    type="button"
                    onClick={updateShipment}
                    className="h-11 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
                  >
                    Simpan & Tandai Dikirim
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </AdminLayout>
  );
};

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <div className="flex items-center gap-3 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
    <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${tone}`}>
      <Icon className="h-5 w-5" />
    </span>
    <div>
      <p className="text-sm text-stone-500">{label}</p>
      <p className="font-display text-xl font-semibold text-stone-900">{value}</p>
    </div>
  </div>
);

const SalesTable = ({ sales, products, onDetail, onShipment, onPrint }) => (
  <div className="space-y-3">
    <div className="hidden overflow-x-auto rounded-2xl border border-stone-200 bg-white shadow-sm md:block">
      <table className="w-full min-w-[1080px] text-left">
        <thead className="bg-stone-50 text-sm text-stone-500">
          <tr>
            <th className="px-5 py-4 font-medium">Invoice</th>
            <th className="px-5 py-4 font-medium">Customer</th>
            <th className="px-5 py-4 font-medium">Produk</th>
            <th className="px-5 py-4 font-medium">Diskon</th>
            <th className="px-5 py-4 font-medium">Total</th>
            <th className="px-5 py-4 font-medium">Status</th>
            <th className="px-5 py-4 font-medium">Tanggal</th>
            <th className="px-5 py-4 text-right font-medium">Aksi</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {sales.map((sale) => (
            <tr key={sale.id}>
              <td className="px-5 py-4 font-medium text-stone-900">{sale.invoice}</td>
              <td className="px-5 py-4">
                <p className="font-medium text-stone-900">{sale.customer.full_name}</p>
                <p className="text-sm text-stone-500">{sale.customer.phone}</p>
              </td>
              <td className="px-5 py-4 text-sm text-stone-700">
                {sale.items.map((item) => `${item.product_name_snapshot} x${item.quantity}`).join(", ")}
              </td>
              <td className="px-5 py-4 text-sm">
                {sale.discount ? (
                  <div>
                    <p className="font-medium text-stone-900">{sale.discount.code}</p>
                    <p className="text-xs text-stone-500">
                      {discountTargetLabel[sale.discount_target_snapshot || sale.discount.discount_target] || "Produk"}
                    </p>
                    <p className="text-rose-600">-{formatRupiah(sale.discount_amount)}</p>
                  </div>
                ) : (
                  <span className="text-stone-400">-</span>
                )}
              </td>
              <td className="px-5 py-4 font-medium text-emerald-700">{formatRupiah(sale.total)}</td>
              <td className="px-5 py-4"><StatusBadge status={sale.order_status} /></td>
              <td className="px-5 py-4 text-sm text-stone-500">{formatDate(sale.payment_completed_at || sale.sale_date)}</td>
              <td className="px-5 py-4 text-right">
                <div className="flex items-center justify-end gap-2">
                  {isPaymentCompleted(sale) && (
                    <Button
                      variant="outline"
                      onClick={() => onPrint(sale, products)}
                      className="h-10 rounded-lg border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                    >
                      <Printer className="mr-1 h-4 w-4" /> Cetak
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => onDetail(sale)} className="h-10 rounded-lg">
                    <Eye className="mr-1 h-4 w-4" /> Detail
                  </Button>
                  {sale.order_status === "diproses" && (
                    <Button variant="outline" onClick={() => onShipment(sale)} className="h-10 rounded-lg">
                      <Truck className="mr-1 h-4 w-4" /> Resi
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="space-y-3 md:hidden">
      {sales.map((sale) => (
        <div key={sale.id} className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-stone-900">{sale.invoice}</p>
              <p className="text-sm text-stone-500">{formatDate(sale.payment_completed_at || sale.sale_date)}</p>
            </div>
            <StatusBadge status={sale.order_status} />
          </div>
          <div className="mt-3 text-sm">
            <p className="font-medium text-stone-900">{sale.customer.full_name}</p>
            <p className="text-stone-500">{sale.customer.phone}</p>
            <p className="mt-2 text-stone-700">
              {sale.items.map((item) => `${item.product_name_snapshot} x${item.quantity}`).join(", ")}
            </p>
            {sale.discount ? (
              <p className="mt-2 text-rose-600">
                {sale.discount.code} -{formatRupiah(sale.discount_amount)}
              </p>
            ) : null}
            <p className="mt-2 font-semibold text-emerald-700">{formatRupiah(sale.total)}</p>
          </div>
          <div className="mt-4 flex gap-2">
            {isPaymentCompleted(sale) && (
              <Button
                variant="outline"
                onClick={() => onPrint(sale, products)}
                className="h-10 flex-1 rounded-lg border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
              >
                <Printer className="mr-1 h-4 w-4" /> Cetak
              </Button>
            )}
            <Button variant="outline" onClick={() => onDetail(sale)} className="h-10 flex-1 rounded-lg">
              <Eye className="mr-1 h-4 w-4" /> Detail
            </Button>
            {sale.order_status === "diproses" && (
              <Button variant="outline" onClick={() => onShipment(sale)} className="h-10 flex-1 rounded-lg">
                <Truck className="mr-1 h-4 w-4" /> Resi
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const Info = ({ label, value, wide }) => (
  <div className={wide ? "sm:col-span-2" : ""}>
    <dt className="text-sm text-stone-500">{label}</dt>
    <dd className="font-medium text-stone-900">{value || "-"}</dd>
  </div>
);

export default AdminSalesPage;
